import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

type Props = {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    /** close when clicking the overlay */
    closeOnOverlayClick?: boolean;
    /** close when pressing Escape */
    closeOnEscape?: boolean;
    /** extra classes for modal panel */
    panelClassName?: string;
    /** extra classes for overlay */
    overlayClassName?: string;
    /** z-index base (overlay z = base, panel z = base+1) */
    zIndexBase?: number;
};

function useBodyScrollLock(locked: boolean) {
    useEffect(() => {
        if (!locked) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [locked]);
}

export function AdminModal({
    open,
    onClose,
    children,
    closeOnOverlayClick = true,
    closeOnEscape = true,
    panelClassName = '',
    overlayClassName = '',
    zIndexBase = 999,
}: Props) {
    useBodyScrollLock(open);

    useEffect(() => {
        if (!open || !closeOnEscape) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, closeOnEscape, onClose]);

    if (!open) return null;

    const overlay = (
        <div
            className={`fixed inset-0 ${overlayClassName}`}
            style={{ zIndex: zIndexBase }}
            aria-hidden="true"
            onMouseDown={(e) => {
                if (!closeOnOverlayClick) return;
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="absolute inset-0" />
            <div
                className={`relative flex min-h-full w-full items-center justify-center p-4 bg-black bg-opacity-50 ${panelClassName}`}
                style={{ zIndex: zIndexBase + 1 }}
                role="dialog"
                aria-modal="true"
            >
                {children}
            </div>
        </div>
    );

    return createPortal(overlay, document.body);
}

